// Story 4.4: Submission Confirmation Page
// Celebrates a successful submission with reference number display

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Clock, Zap } from 'lucide-react';
import { useClaim } from '../../hooks/useClaims';
import { CelebrationAnimation } from '../../components/ui/CelebrationAnimation';
import { Card, Button } from '@rhpc/ui';

function formatCurrency(amount: number | string) {
  return new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(amount));
}

function formatMonth(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', { year: 'numeric', month: 'long', timeZone: 'Asia/Kuala_Lumpur' });
}

function formatSubmittedAt(isoDate: string) {
  return new Date(isoDate).toLocaleString('en-MY', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const NEXT_STEPS = [
  'Admin reviews and screens your claim',
  'Finance reviews and approves your claim',
  'HR gives final approval for payment',
  "You'll be notified at each stage",
];

export function SubmissionConfirmationPage() {
  const { claimId } = useParams<{ claimId: string }>();
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);
  const [elapsed, setElapsed] = useState<number | null>(null);

  const { data: claimData, isLoading } = useClaim(claimId!);
  const claim = claimData?.claim;

  useEffect(() => {
    const startTime = sessionStorage.getItem(`claim_start_${claimId}`);
    if (startTime) {
      const seconds = Math.round((Date.now() - parseInt(startTime)) / 1000);
      setElapsed(seconds);
      sessionStorage.removeItem(`claim_start_${claimId}`);
    }
  }, [claimId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="py-20 text-center">
        <p className="text-text-2">Claim not found</p>
        <button onClick={() => navigate('/claims')} className="mt-3 text-sm text-primary underline">
          ← Back to Claims
        </button>
      </div>
    );
  }

  return (
    <>
      {showConfetti && <CelebrationAnimation duration={3500} onComplete={() => setShowConfetti(false)} />}

      <div className="mx-auto flex max-w-[640px] flex-col items-center py-8 text-center">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-ok-soft text-ok">
          <Check className="h-8 w-8" strokeWidth={2.4} />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Claim submitted</h1>
        <p className="mt-2 text-base text-text-2">Your claim has been sent for admin review.</p>

        <Card className="mt-8 w-full p-[28px_30px] text-center">
          <div className="text-xs font-semibold uppercase tracking-wider text-text-2">
            Reference Number
          </div>
          <div className="mt-2.5 font-mono text-3xl font-semibold tracking-wide">
            {claim.referenceNumber ?? '—'}
          </div>
          <div className="mt-2 text-sm text-text-2">Keep this number for your records</div>
        </Card>

        <Card className="mt-6 w-full p-[28px_30px] text-left">
          <h2 className="mb-5 text-base font-semibold">Claim Summary</h2>
          <div className="mb-5 grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-text-2">Period</div>
              <div className="mt-1 text-lg font-semibold">{formatMonth(claim.claimMonth)}</div>
            </div>
            <div>
              <div className="text-sm text-text-2">Total Amount</div>
              <div className="mt-1 font-mono text-lg font-semibold">
                RM {formatCurrency(claim.totalAmount)}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-[5px] rounded-full bg-info-soft px-3 py-1 text-xs font-semibold text-info">
              <Clock className="h-3 w-3" strokeWidth={2.4} />
              Pending Admin Review
            </span>
            {elapsed !== null && elapsed > 0 && (
              <span className="inline-flex items-center gap-[5px] rounded-full bg-surface-2 px-3 py-1 text-xs font-semibold text-text-3">
                <Zap className="h-3 w-3" strokeWidth={2.4} />
                Submitted in {elapsed}s
              </span>
            )}
            <span className="inline-flex items-center gap-[5px] rounded-full bg-surface-2 px-3 py-1 text-xs font-semibold text-text-3">
              <Clock className="h-3 w-3" strokeWidth={2.4} />
              Submitted {formatSubmittedAt(claim.updatedAt)}
            </span>
          </div>
        </Card>

        <Card className="mt-6 w-full p-[28px_30px] text-left">
          <h2 className="mb-5 text-base font-semibold">What happens next?</h2>
          <div className="flex flex-col gap-4">
            {NEXT_STEPS.map((text, i) => (
              <div key={text} className="flex items-center gap-3.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-2 text-sm font-bold">
                  {i + 1}
                </div>
                <span className="text-[15px]">{text}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="mt-7 flex w-full gap-3">
          <Button variant="outline" size="lg" className="flex-1" onClick={() => navigate(`/claims/${claimId}`)}>
            View Claim
          </Button>
          <Button size="lg" className="flex-1" onClick={() => navigate('/claims')}>
            Back to Claims
          </Button>
        </div>
      </div>
    </>
  );
}
